"use client";

import type { AddToCartEventData } from "@commerce-frontend/gtm-ecommerce";
import { countryOf } from "@commerce-frontend/i18n/helpers";
import { usePathname } from "@commerce-frontend/i18n/navigation";
import type { Locale } from "@commerce-frontend/i18n/types";
import type { CombinedProductAnalyticsFragmentFragment } from "@commerce-frontend/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { byMax } from "@labdigital/toolkit";
import { useLocale, useTranslations } from "next-intl";
import type React from "react";
import { useAddToCartContext } from "~/components/AddToCartContext";
import { useAddLineItems } from "~/hooks/cart/useAddLineItems";
import { useCart } from "~/hooks/cart/useCart";
import { saveObjectID } from "~/lib/helpers/insights";
import { useInsights } from "~/lib/insights-provider";
import { useNavigation } from "~/lib/navigation-provider";
import {
	deviceData,
	ecommerceData,
	locationDataFromPathname,
	sendGTMEvent,
	userData,
} from "~/lib/send-gtm-event";

type Props = {
	sku: string;
	quantity: number;
	categoryId?: string | undefined;
	product: CombinedProductAnalyticsFragmentFragment;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
	React.AnchorHTMLAttributes<HTMLAnchorElement>;

export function AddToCartButton({ sku, product, quantity, categoryId, ...attributes }: Props) {
	const { cart } = useCart();
	const { mutate: addLineItems, isPending: addLineItemsPending } = useAddLineItems();
	const { setAddedLineItem, setErrorOpen } = useAddToCartContext();
	const t = useTranslations("AddToCartButton");
	const locale = useLocale() as Locale;
	const pathname = usePathname();
	const { previousUrl } = useNavigation();
	const { sendInsightsEvent, queryID } = useInsights();

	const addToCart = async (sku: string, quantity: number) => {
		sendGTMEvent<AddToCartEventData>({
			event: "add_to_cart",
			timestamp: Date.now(),
			device: deviceData(),
			user: userData(),
			location: locationDataFromPathname({
				locale,
				pathname,
				categoryId,
				previousUrl,
			}),
			ecommerce: ecommerceData([
				{
					product,
					sku,
					quantity,
				},
			]),
		});

		if (queryID) {
			sendInsightsEvent("convertedObjectIDsAfterSearch", {
				eventName: "Product Converted",
				index: `${countryOf(locale).toLowerCase()}_default`,
				objectIDs: [product.id],
				queryID: queryID,
			});

			sendInsightsEvent("addedToCartObjectIDsAfterSearch", {
				eventName: "Product Added to Cart",
				index: `${countryOf(locale).toLowerCase()}_default`,
				objectIDs: [product.id],
				queryID: queryID,
			});

			saveObjectID({
				objectID: product.id,
				queryID: queryID,
			});
		}

		addLineItems(
			{
				id: cart?.id,
				lineItems: [{ sku, quantity }],
			},
			{
				onSuccess: (data) => {
					if (data.errors?.length) {
						setErrorOpen(true);
						return;
					}

					const lineItem = data?.data?.cartLineItemsAdd.lineItems
						.filter((l) => l?.variant.sku === sku)
						.reduce(byMax((l) => (l.addedAt ? new Date(l.addedAt).getTime() : undefined)));

					if (lineItem) {
						setAddedLineItem(lineItem);
					}
				},
				onError: () => {
					setErrorOpen(true);
				},
			},
		);
	};

	return (
		<Button
			{...attributes}
			disabled={addLineItemsPending}
			variant="primary"
			size="xl"
			onClick={() => void addToCart(sku, quantity)}
		>
			<Icon icon="plus" className="size-5 bg-white" />
			{t("add-to-cart")}
		</Button>
	);
}
